import { CURRENCIES } from 'types/bussines';
import {
  IOrderPriceEdition,
  IOrderPriceEditionItem,
  IOrderPriceEditionProductConfigFeeItem,
  IOrderProductConfigPrice,
  IOrderWithProductPrices
} from 'types/orders';
import { IOrderPriceEditionForm } from 'views/orders/OrderPriceEdition/OrderPriceEdition.types';

export const EDITION_FORM_DEFAULT_VALUES: IOrderPriceEditionForm = {
  sales: [],
  exchangeRate: 0,
  currency: CURRENCIES.USD,
  subtotal: 0,
  totalUsd: 0,
  totalLocal: 0,
  iva: 0,
  reason: undefined,
  commissionUsd: 0,
  shippingValueUsd: 0,
  shipmentTaxUsd: 0
};

const removeTaxesFromValue = (value: number, taxPercentage: number) => {
  if (!value || !taxPercentage) {
    return value;
  }
  const divisor = 1 + taxPercentage / 100;
  if (divisor <= 0) {
    return value;
  }
  return Number((value / divisor).toFixed(2));
};

export const getOrderProductConfigUsdAmount = (pc: Partial<IOrderProductConfigPrice>): number =>
  Number(pc.totalUsd ?? pc.totalValueUsd ?? pc.priceUsd) || 0;

/**
 * Arma los ítems de fee por configuración. Los montos USD son los del formulario sin ajuste fiscal
 * (el IVA de orden solo se aplica a la línea de herramienta al persistir).
 */
const buildProductConfigFeeItems = (
  data: IOrderPriceEditionForm,
  orderData: IOrderWithProductPrices
): IOrderPriceEditionProductConfigFeeItem[] => {
  return data.sales.flatMap(sale => {
    if (!sale.id) return [];
    return (sale.orderProductPrices || [])
      .filter(pc => pc.configClassFeeId || pc.id)
      .map(pc => {
        const feeId = pc.configClassFeeId ?? pc.id;
        const currentUsd = getOrderProductConfigUsdAmount(pc);
        const originalSale = orderData.sales?.find(s => s.id === sale.id);
        const originalPc = originalSale?.orderProductPrices?.find(
          o => o.configClassFeeId === feeId || o.id === pc.id
        );
        const previousUsd = originalPc ? getOrderProductConfigUsdAmount(originalPc) : 0;
        return {
          productConfigId: pc.productConfigId ?? pc.id,
          currency: String(data.currency) || CURRENCIES.USD,
          exchangeRate: Number(pc.exchangeRate) || Number(data.exchangeRate) || 1,
          previousUnitAmount: previousUsd,
          newUnitAmount: currentUsd,
          totalLocalAmount: Number(pc.totalValueLocal) || 0
        };
      });
  });
};

const buildPriceEditionItems = (
  data: IOrderPriceEditionForm,
  orderData?: IOrderWithProductPrices,
  shouldApplyTaxes?: boolean
): IOrderPriceEditionItem[] => {
  const taxPercentage = shouldApplyTaxes ? Number(orderData?.orderPrice?.tax ?? 0) : 0;
  return data.sales
    .filter(sale => sale.id)
    .map(sale => {
      const saleValueUsd = Number(sale.saleValueUsd) || 0;
      return {
        saleId: sale.id as number,
        currency: String(data.currency) || CURRENCIES.USD,
        previousUnitAmount: Number(orderData?.sales?.find(currSale => currSale.id === sale.id)?.saleValueUsd || 0),
        newUnitAmount: removeTaxesFromValue(saleValueUsd, taxPercentage),
        totalLocalAmount: Number(sale.saleValue) || 0
      };
    });
};

export type BuildPriceEditionPayloadOptions = {
  /**
   * Si es false, no se incluyen ítems de fee por configuración en el payload (`productConfigFeeItems` vacío).
   * Útil cuando el flujo onlyDevs muestra edición por configuración en UI pero el backend aún no debe recibir ese arreglo.
   */
  includeProductConfigFeeItems?: boolean;
};

export const buildPriceEditionPayload = (
  data: IOrderPriceEditionForm,
  orderData?: IOrderWithProductPrices,
  shouldApplyTaxes?: boolean,
  options?: BuildPriceEditionPayloadOptions
): IOrderPriceEdition | null => {
  if (!orderData) return null;
  const includeFeeItems = options?.includeProductConfigFeeItems !== false;
  const productConfigFeeItems = includeFeeItems ? buildProductConfigFeeItems(data, orderData) : [];
  return {
    orderId: orderData.id,
    currency: data.currency,
    previousTotalAmount: Number(orderData?.orderPrice?.totalValueUsd),
    newTotalAmount: data.totalUsd,
    totalLocalAmount: data.totalLocal,
    reason: data.reason,
    exchangeRate: Number(data.exchangeRate) || 1,
    commissionUsd: Number(data.commissionUsd || 0),
    changedAt: new Date().toISOString(),
    needInvoice: true,
    previousInvoiceUrl: orderData?.orderSummaryPDF,
    items: buildPriceEditionItems(data, orderData, shouldApplyTaxes),
    productConfigFeeItems
  };
};

export const initializePriceEditionForm = (orderData?: IOrderWithProductPrices): IOrderPriceEditionForm => ({
  sales: (orderData?.sales || [])?.map(sale => ({
    ...sale,
    orderProductPrices: sale?.orderProductPrices ?? []
  })),
  currency: orderData?.orderPrice?.currency || CURRENCIES.USD,
  exchangeRate: Number(orderData?.orderPrice?.exchangeRate) || 0,
  subtotal: Number(orderData?.orderPrice?.subtotalSaleValueUsd) || 0,
  totalUsd: Number(orderData?.orderPrice?.totalValueUsd) || 0,
  totalLocal: Number(orderData?.orderPrice?.totalValue) || 0,
  iva: Number(orderData?.orderPrice?.totalTaxesUsd || 0),
  commissionUsd: Number(orderData?.orderPrice?.commissionValueUsd || 0),
  shippingValueUsd: Number(orderData?.orderPrice?.shipmentValueUsd || 0),
  shipmentTaxUsd: Number(orderData?.orderPrice?.shipmentTaxUsd ?? 0),
  levelSubscription: orderData?.organizationData?.platformSubscription || undefined
});
